'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

export default function TestimonialsSection() {
  const testimonials = [
    {
      id: 1,
      name: 'Abhi & Niyu',
      position: 'Youtube Content Creators',
      quote: 'I attended the workshop & I loved it and would give it 5/5 stars.',
      image: 'https://d3kl8zsmmx4oop.cloudfront.net/image_1490_Background_Removed_2_c1936352c9.png',
    },
    {
      id: 2,
      name: 'Ankur Warikoo',
      position: 'Entrepreneur, Content Creator & Author',
      quote: 'I know Vaibhav as the guy who is killing it on LinkedIn, because of everything he is doing and he understands the platform so well.',
      image: 'https://d3kl8zsmmx4oop.cloudfront.net/Rectangle_6106_054b64ebae.svg',
    },
    {
      id: 3,
      name: 'Tanmay Bhat',
      position: 'Youtuber, Comedian & Investor',
      quote: 'The strategies taught in this program helped me grow my personal brand exponentially.',
      image: 'https://d3kl8zsmmx4oop.cloudfront.net/Rectangle_6106_c04843af48.png',
    },
    {
      id: 4,
      name: 'Raj Shamani',
      position: 'Entrepreneur, Podcast Host & Author',
      quote: "Vaibhav understands growth, it's like it's in his DNA. Reach, engagement, scale - you name it & he has a growth hack for everything.",
      image: 'https://d3kl8zsmmx4oop.cloudfront.net/Rectangle_6104_e4be586a32.png',
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [thumbnailIndices, setThumbnailIndices] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef(null);
  const resetTransitionRef = useRef(null);

  // Update thumbnail indices when activeIndex changes
  useEffect(() => {
    const indices = [];
    for (let i = 1; i <= 3; i++) {
      indices.push((activeIndex + i) % testimonials.length);
    }
    setThumbnailIndices(indices);
  }, [activeIndex, testimonials.length]);

  // Navigate to a specific testimonial
  const goToTestimonial = (index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    resetTransitionRef.current = setTimeout(() => {
      setActiveIndex(index);
      setIsTransitioning(false);
    }, 500);
  };

  // Auto-rotate every 5s
  useEffect(() => {
    autoplayRef.current = setInterval(() => {
      const nextIndex = (activeIndex + 1) % testimonials.length;
      goToTestimonial(nextIndex);
    }, 5000);

    return () => {
      clearInterval(autoplayRef.current);
      clearTimeout(resetTransitionRef.current);
    };
  }, [activeIndex, testimonials.length]);

  return (
    <section className="w-full py-16 bg-white">
      <div className="w-[90%] md:w-[80%] mx-auto">
        <div className="flex flex-col md:flex-row md:items-start gap-8">
          {/* Left side - Section title */}
          <div className="md:w-[40%]">
            <p className="text-sm md:text-base uppercase font-medium tracking-wide text-gray-600 mb-4">
              GLOBALLY ACCLAIMED
            </p>
            <h2 className="text-3xl md:text-[36px] font-medium leading-tight">
              Recommended by your Favourite Influencers and Creators
            </h2>
          </div>

          {/* Right side - Testimonial card */}
          <div className="md:w-[60%]">
            <div className="relative rounded-2xl bg-[#142f2e] overflow-hidden">
              {/* Bottom-align image on md+ and reduce padding */}
              <div className="flex flex-col md:flex-row p-6 md:items-end">
                {/* Profile image */}
                <div className="md:w-[45%] flex justify-center md:justify-start items-end mb-6 md:mb-0">
                  <div className="w-48 h-52 relative">
                    <div
                      className="absolute inset-0 rounded-full overflow-hidden"
                      style={{
                        backgroundColor: '#1b3c3a',
                        transform: 'scale(0.9)',
                      }}
                    >
                      <div className="relative w-full h-full transition-opacity duration-500">
                        {testimonials.map((t, i) => (
                          <div
                            key={t.id}
                            className={`absolute inset-0 transition-opacity duration-500 ${
                              activeIndex === i && !isTransitioning
                                ? 'opacity-100 z-10'
                                : 'opacity-0 z-0'
                            }`}
                          >
                            <Image
                              src={t.image}
                              alt={t.name}
                              fill
                              sizes="(max-width: 768px) 192px, 192px"
                              className="object-cover object-center"
                              priority={i === 0}
                              loading={i === 0 ? "eager" : "lazy"}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="md:w-[55%] flex flex-col justify-center">
               
                  {/* Reduced min-height */}
                  <div className="relative min-h-[200px]">
                    {testimonials.map((t, i) => (
                      <div
                        key={t.id}
                        className={`absolute inset-0 transition-opacity duration-500 ${
                          activeIndex === i && !isTransitioning
                            ? 'opacity-100 z-10'
                            : 'opacity-0 z-0'
                        }`}
                      >
                        <p className="text-white text-lg md:text-xl mb-6">
                          {t.quote}
                        </p>
                        <div className="mb-6">
                          <h3 className="text-white text-xl font-medium">
                            {t.name}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {t.position}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Thumbnail navigation */}
                  <div className="flex items-center space-x-4">
                    {thumbnailIndices.map((idx) => (
                      <button
                        key={`thumb-${testimonials[idx].id}`}
                        onClick={() => goToTestimonial(idx)}
                        className="w-10 h-10 relative rounded-full overflow-hidden border border-transparent hover:border-white/30 focus:border-white/30 transition-all"
                        style={{ backgroundColor: '#1b3c3a' }}
                      >
                        <div className="absolute inset-0" style={{ transform: 'scale(0.9)' }}>
                          <Image
                            src={testimonials[idx].image}
                            alt={testimonials[idx].name}
                            fill
                            sizes="40px"
                            className="object-cover object-center"
                            loading="lazy"
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          
          </div>
        </div>
      </div>
    </section>
  );
}